import * as React from "react";
import { PlusCircle, X } from "lucide-react";
import { Input, Button } from "../..";
import { TableFilter, type TableFilterItem } from "./table-filter";
import { Z_INDEX_CLASSES } from "../../lib/z-index";

export interface TableSearchProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onFilterClick?: () => void;
  showFilter?: boolean;
  filterLabel?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  // Filter-specific props
  filterItems?: TableFilterItem[];
  selectedFilters?: string[];
  onFilterSelectionChange?: (selectedFilters: string[]) => void;
  filterPlaceholder?: string;
  isFilterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
}

export function TableSearch({
  placeholder = "Search...",
  value = "",
  onChange,
  onFilterClick,
  showFilter = false,
  filterLabel = "Filter",
  className = "",
  inputClassName = "",
  disabled = false,
  // Filter-specific props
  filterItems = [],
  selectedFilters = [],
  onFilterSelectionChange,
  isFilterOpen = false,
  onFilterOpenChange,
}: TableSearchProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [dropdownRef, setDropdownRef] = React.useState<HTMLDivElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value);
  };

  const handleToggleOpen = () => {
    if (filterItems.length > 0 && onFilterSelectionChange) {
      const newOpen = !internalOpen;
      setInternalOpen(newOpen);
      onFilterOpenChange?.(newOpen);
    } else {
      onFilterClick?.();
    }
  };

  const handleClickOutside = React.useCallback(
    (event: MouseEvent) => {
      if (
        dropdownRef &&
        !dropdownRef.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setInternalOpen(false);
        onFilterOpenChange?.(false);
      }
    },
    [dropdownRef, onFilterOpenChange]
  );

  React.useEffect(() => {
    if (internalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [internalOpen, handleClickOutside]);

  React.useEffect(() => {
    if (isFilterOpen !== undefined) {
      setInternalOpen(isFilterOpen);
    }
  }, [isFilterOpen]);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        className={`max-w-sm ${inputClassName}`}
        size="small"
        disabled={disabled}
      />
      {showFilter && (
        <div className="relative">
          <Button
            ref={buttonRef}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 border-dashed"
            style={{ borderStyle: "dashed" }}
            onClick={handleToggleOpen}
            disabled={disabled}
          >
            <PlusCircle className="h-4 w-4" />
            {filterLabel}
            {selectedFilters.length === 1 && (
              <span className="ml-1 bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-md">
                {selectedFilters[0]}
              </span>
            )}
            {selectedFilters.length === 2 && (
              <div className="ml-1 flex items-center gap-1">
                <span className="bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-md">
                  {selectedFilters[0]}
                </span>
                <span className="bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-md">
                  {selectedFilters[1]}
                </span>
              </div>
            )}
            {selectedFilters.length >= 3 && (
              <span className="ml-1 bg-accent text-accent-foreground text-xs px-2 py-1 rounded-md">
                {selectedFilters.length} selected
              </span>
            )}
          </Button>

          {filterItems.length > 0 && onFilterSelectionChange && internalOpen && (
            <div
              ref={setDropdownRef}
              className={`absolute left-0 top-full mt-1 w-80 rounded-md border bg-popover shadow-md ${Z_INDEX_CLASSES.dropdown}`}
            >
              <TableFilter
                items={filterItems}
                selectedItems={selectedFilters}
                onSelectionChange={onFilterSelectionChange}
                hideButtons
                className="border-0 shadow-none"
              />
            </div>
          )}
        </div>
      )}
      {selectedFilters.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-2"
          onClick={() => onFilterSelectionChange?.([])}
        >
          Reset
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
